import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Car } from 'lucide-react';
import { Button } from './ui/button';
import { Combobox } from './ui/combobox';
import { makesAPI, modelsAPI, enginesAPI } from '../services/api';

const formatCC = (cc) => {
  if (cc >= 1000) return `${(cc / 1000).toFixed(1)}L (${cc}cc)`;
  return `${cc}cc`;
};

/**
 * Find My Vehicle — cascading picker.
 * Make → Model → Engine Size (optional). Jumps to filtered products.
 */
const FindMyVehicle = () => {
  const navigate = useNavigate();
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [make, setMake] = useState('all');
  const [model, setModel] = useState('all');
  const [cc, setCc] = useState('all');
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingCcs, setLoadingCcs] = useState(false);

  useEffect(() => {
    makesAPI.getAll().then(setMakes).catch(() => {});
  }, []);

  useEffect(() => {
    if (make === 'all') { setModels([]); setModel('all'); return; }
    setLoadingModels(true);
    modelsAPI.getAll(make)
      .then(setModels)
      .finally(() => setLoadingModels(false));
    setModel('all');
    setCc('all');
  }, [make]);

  useEffect(() => {
    if (model === 'all') { setCcs([]); setCc('all'); return; }
    setLoadingCcs(true);
    enginesAPI.getCCs(make, model)
      .then(setCcs)
      .finally(() => setLoadingCcs(false));
    setCc('all');
  }, [make, model]);

  const handleFind = () => {
    const params = new URLSearchParams();
    if (make !== 'all') params.set('make', make);
    if (model !== 'all') params.set('model', model);
    if (cc !== 'all') params.set('cc', cc);
    navigate(`/products?${params.toString()}`);
  };

  const reset = () => { setMake('all'); setModel('all'); setCc('all'); };
  const canFind = make !== 'all';

  const ccOptions = ccs.map((c) => ({ value: String(c), label: formatCC(c) }));

  return (
    <section className="bg-gradient-to-br from-blue-600 to-blue-800 py-12" data-testid="find-my-vehicle">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-8 -mt-20 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Car className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">Find Your Vehicle</h2>
              <p className="text-sm text-gray-500">Pick your make, model and (optionally) engine size to see compatible modules.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">1. Make</label>
              <Combobox
                value={make}
                onChange={setMake}
                options={makes}
                placeholder="Make"
                allLabel="Any make"
                className="w-full"
                testId="fmv-make"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">2. Model</label>
              <Combobox
                value={model}
                onChange={setModel}
                options={models}
                placeholder={loadingModels ? 'Loading…' : 'Model'}
                allLabel="Any model"
                className="w-full"
                disabled={make === 'all' || loadingModels}
                testId="fmv-model"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                3. Engine Size <span className="text-gray-400">(optional)</span>
              </label>
              <Combobox
                value={cc}
                onChange={setCc}
                options={ccOptions}
                placeholder={loadingCcs ? 'Loading…' : 'Engine size'}
                allLabel="Any size"
                className="w-full"
                disabled={model === 'all' || loadingCcs}
                testId="fmv-cc"
              />
            </div>
            <div className="flex flex-col gap-2 justify-end">
              <Button onClick={handleFind} disabled={!canFind} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="fmv-find-btn">
                <Search className="h-4 w-4 mr-2" /> Find Modules
              </Button>
              {(make !== 'all' || model !== 'all' || cc !== 'all') && (
                <button
                  onClick={reset}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                  data-testid="fmv-reset"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FindMyVehicle;
